import { useState, useCallback } from 'react'
import { getActivePoll, getMySubmission, retryPollSetup, submitPoll } from '../api'
import { Poll, PollAccessState, PollAnswerItem, PollSetupRun, PollSubmission } from '../types'

interface UsePollState {
  poll: Poll | null
  access: PollAccessState | null
  submission: PollSubmission | null
  setup: PollSetupRun | null
  loading: boolean
  submitting: boolean
  error: string | null
}

export function usePoll() {
  const [state, setState] = useState<UsePollState>({
    poll: null,
    access: null,
    submission: null,
    setup: null,
    loading: false,
    submitting: false,
    error: null,
  })

  const loadPoll = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const poll = await getActivePoll()
      setState(prev => ({ ...prev, poll, loading: false }))
      return poll
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error?.message || 'Не удалось загрузить опросник',
      }))
      return null
    }
  }, [])

  const checkSubmission = useCallback(async () => {
    try {
      const access = await getMySubmission()
      setState(prev => ({
        ...prev,
        access,
        submission: access?.submission || null,
        setup: access?.setup || null,
      }))
      return access
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        access: {
          needs_poll: false,
          is_tenant_owner: false,
          submission: null,
          setup: null,
        },
        error: error?.message || 'Не удалось проверить статус опросника',
      }))
      return null
    }
  }, [])

  const submit = useCallback(async (answers: PollAnswerItem[]) => {
    setState(prev => ({ ...prev, submitting: true, error: null }))
    try {
      const submission = await submitPoll(answers, 'completed')
      setState(prev => ({
        ...prev,
        submission,
        setup: submission?.setup || null,
        submitting: false,
      }))
      return submission
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        submitting: false,
        error: error?.message || 'Не удалось отправить опросник',
      }))
      return null
    }
  }, [])

  const retrySetup = useCallback(async () => {
    setState(prev => ({ ...prev, submitting: true, error: null }))
    try {
      const setup = await retryPollSetup()
      setState(prev => ({ ...prev, setup, submitting: false }))
      return setup
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        submitting: false,
        error: error?.message || 'Не удалось повторить настройку',
      }))
      return null
    }
  }, [])

  return {
    ...state,
    loadPoll,
    checkSubmission,
    submit,
    retrySetup,
  }
}
